import { useEffect, useLayoutEffect, useRef } from "react";
import { canAddToCell, type BoardDay } from "../lib/programBoard";
import { useTopLayerMenu } from "./useTopLayerMenu";

/* ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════
 * THE BOARD'S ADD — ONE `+` AND ONE PICKER FOR BOTH VIEWS (his report 2026-10-31: "why can't I add any workouts in the
 * programs? … there's no button or anything to assign a workout in a day, if we have the extended view").
 *
 * MEASURED before this file: Minimized hid its `+` until the cell was hovered, so an empty week looked dead; Extended drew
 * the day column with NO add at all. One component now, used by both, so the two views cannot drift again:
 *   · an EMPTY day shows its add at rest, in the builder's own add shape (`Add Set`'s outlined box) — `+` in a square
 *     cell, `+ Workout` at the head of an empty column (the column's own idiom: the week editor's `+ Exercise`);
 *   · a FILLED day keeps the quiet, hover-revealed `+` (W22(g)) — in the DOM always, revealed by opacity, never mounted.
 * The picker is the board's one source (the coach's own live templates), and `canAddToCell` refuses a duplicate BEFORE it
 * is offered, disabled and saying why.
 * 🔴 THE PICKER LIVES IN THE TOP LAYER (`useTopLayerMenu`, W79): Extended's day column scrolls its own body, and an
 * absolute picker inside it was a picker the column clipped. A square cell's picker still drops BELOW the cell (§4's
 * recorded defect: over the cell it hid what Monday already held); a column's drops below its own `+`.
 * ══════════════════════════════════════════════════════════════════════════════════════════════════════════════════════ */

export function BoardCellAdd({ view, week, weekday, dayName, empty, open, busy, days, templates, onToggle, onClose, onPick }: {
  /** `cell` — Minimized's square cell; `column` — Extended's day column. */
  view: "cell" | "column";
  week: number;
  weekday: number;
  /** The weekday in full (`Monday`), for the names a screen reader hears. */
  dayName: string;
  /** The day holds no workout: the add is drawn at rest. */
  empty: boolean;
  open: boolean;
  busy: boolean;
  days: readonly BoardDay[] | null;
  templates: readonly { id: string; name: string }[];
  onToggle: () => void;
  onClose: () => void;
  onPick: (workoutId: string) => void;
}) {
  const label = `Add a workout to ${dayName}, week ${String(week)}`;
  const trigger = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLDivElement>(null);
  /* Where the picker hangs: under the whole square cell (never over its cards), else under the `+` itself. Set before the
     top-layer hook places it (layout effects run in declaration order). */
  const anchor = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    if (!open) return;
    const t = trigger.current;
    (anchor as { current: HTMLElement | null }).current = view === "cell" ? t?.closest<HTMLElement>(".program-cell") ?? t : t;
  }, [open, view]);
  useTopLayerMenu(open, anchor, list);

  /* Escape closes and hands focus back to the `+`; a press anywhere else closes (a menu a coach has to obey is not one).
     `onClose` is read through a ref: the board hands a new function each render, and an effect keyed on it would re-run —
     and re-focus the first row — on every render while the picker is open. */
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLButtonElement>(".program-picker-item:not(:disabled)")?.focus();
    const away = (e: PointerEvent) => {
      if (list.current?.contains(e.target as Node) === true || trigger.current?.contains(e.target as Node) === true) return;
      close.current();
    };
    document.addEventListener("pointerdown", away, true);
    return () => { document.removeEventListener("pointerdown", away, true); };
  }, [open]);

  return (
    <>
      <button ref={trigger} type="button" disabled={busy}
              className={`program-cell-add is-${view}${empty ? " is-empty" : ""}`}
              aria-label={label} aria-expanded={open} aria-haspopup="dialog"
              onClick={onToggle}>
        <span aria-hidden="true">+</span>
        {view === "column" && <span aria-hidden="true"> Workout</span>}
      </button>
      {open && (
        <div ref={list} className="program-picker is-top-layer" role="dialog" aria-label={label}
             onKeyDown={(e) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); trigger.current?.focus(); } }}>
          {templates.length === 0 ? (
            <p className="program-picker-empty">No templates yet. Build one in the Workouts library.</p>
          ) : (
            <ul className="program-picker-list">
              {templates.map((tpl) => {
                /* 🔴 THE DUPLICATE IS REFUSED BEFORE IT IS OFFERED. The unique key
                   `(program_id, week_index, weekday, workout_id)` makes one template twice on one day unrepresentable
                   (§2.2) — the DATABASE would refuse the second with 23505 — so it is disabled and SAYS WHY, not hidden. */
                const free = canAddToCell(days ?? [], week, weekday, tpl.id);
                return (
                  <li key={tpl.id}>
                    <button type="button" className="program-picker-item"
                            disabled={!free || busy}
                            title={free ? undefined : "Already on this day"}
                            onClick={() => { onPick(tpl.id); }}>
                      {tpl.name}
                      {!free && <span className="program-picker-already"> · already here</span>}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </>
  );
}
